import { Injectable, inject } from '@angular/core'
import { RestClientPaged } from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Observable, of, switchMap } from 'rxjs'
import { HttpClient } from '@angular/common/http'
import { NewStorageOperation } from './new-storage-operation'
import { environment } from '../../../../environments/environment'
import {
  StorageOperation,
  StorageOperationResult,
  StorageOperationResultType,
} from './storage-operation'
import { filter, mergeMap } from 'rxjs/operators'
import { StorageNode } from '../nodes/storage-node'
import {
  IStorageNodeSelectionDialogOptions,
  StorageNodeSelectionDialogComponent,
} from '../nodes/storage-node-selection-dialog/storage-node-selection-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { Package } from '../packages/package'
import { StorageBrowseOptions } from '../nodes/storage-node-browser/storage-node-browse-options'
import Retraction = NewStorageOperation.Retraction
import CheckIn = NewStorageOperation.CheckIn
import CheckOut = NewStorageOperation.CheckOut
import Movement = NewStorageOperation.Movement

@Injectable({
  providedIn: 'root',
})
export class StorageOperationsService extends RestClientPaged<NewStorageOperation, string> {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const http = inject(HttpClient)

    super(environment.apiPath + '/storage/operations', http, {
      idField: 'id',
    })
  }

  /***************************************************************************
   *                                                                         *
   * Check-in                                                                *
   *                                                                         *
   **************************************************************************/

  public checkIn(checkIn: CheckIn): Observable<StorageOperation> {
    const request = NewStorageOperation.checkIn(checkIn)

    return this.createRequest(request).pipe(
      mergeMap((operation) => this.handleCheckInResult(operation))
    )
  }

  private handleCheckInResult(operation: StorageOperation): Observable<StorageOperation> {
    if (operation.result.resultType === StorageOperationResultType.INPUT_REQUIRED) {
      return this.handleStorageInputRequired(operation.request.checkIn, operation.result)
    } else {
      return of(operation)
    }
  }

  private handleStorageInputRequired(
    checkIn: CheckIn,
    result: StorageOperationResult
  ): Observable<StorageOperation> {
    const options = StorageBrowseOptions.start().withBrowsePath(checkIn.baseLocation)

    if (result.suitedLocations) {
      options.withSelectableNodeIds(result.suitedLocations.map((l) => l.id))
    }

    return this.openNodeSelectionChooser(options).pipe(
      filter((node) => !!node),
      mergeMap((node) => {
        checkIn.targetLocation = node.path
        return this.checkIn(checkIn)
      })
    )
  }

  private openNodeSelectionChooser(options?: StorageBrowseOptions): Observable<StorageNode> {
    return this.dialog
      .open(StorageNodeSelectionDialogComponent, {
        width: '80vw',
        height: '80vh',
        disableClose: true,
        data: <IStorageNodeSelectionDialogOptions>{
          storageBrowseOptions: options,
        },
      })
      .afterClosed()
  }

  /***************************************************************************
   *                                                                         *
   * Retract                                                                 *
   *                                                                         *
   **************************************************************************/

  public retract(packageIds: string[]): Observable<StorageOperation> {
    const request = NewStorageOperation.retract(new Retraction(packageIds))
    return this.createRequest(request)
  }

  /***************************************************************************
   *                                                                         *
   * Check out                                                               *
   *                                                                         *
   **************************************************************************/

  public checkOut(packageIds: string[]): Observable<StorageOperation> {
    const request = NewStorageOperation.checkOut(new CheckOut(packageIds))
    return this.createRequest(request)
  }

  /***************************************************************************
   *                                                                         *
   * Move                                                                    *
   *                                                                         *
   **************************************************************************/

  public chooseLocationAndMove(
    packages: Package[],
    storageBrowseOptions?: StorageBrowseOptions
  ): Observable<StorageOperation> {
    return this.openNodeSelectionChooser(storageBrowseOptions).pipe(
      filter((selectedNode) => !!selectedNode),
      switchMap((selectedNode) => this.move(packages, selectedNode))
    )
  }

  public move(packages: Package[], targetLocation: StorageNode): Observable<StorageOperation> {
    const packageIds = packages.map((pkg) => pkg.id)
    const movement = new Movement(
      {
        ids: packageIds,
      },
      targetLocation.path
    )
    return this.performMovement(movement)
  }

  public performMovement(movement: Movement): Observable<StorageOperation> {
    const request = NewStorageOperation.move(movement)
    return this.createRequest(request)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public createRequest(request: NewStorageOperation): Observable<StorageOperation> {
    this.log.debug('Sending storage operation request:', request)
    return this.http.post<StorageOperation>(this.restEndpoint, request)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/
}
